<template>
  <div>
    <Movementmenu />
    <v-container fluid class="grey lighten-4 pa-6">
      <!-- ═══════════════════════════════════════════════════════
           1) Summary Cards (สรุปภาพรวมอัตรากำลัง)
           ═══════════════════════════════════════════════════════ -->
      <v-row dense class="mb-4">
        <v-col
          v-for="card in summaryCards"
          :key="card.key"
          cols="12"
          :md="card.md || 2"
        >
          <v-card class="rounded-lg shadow-sm" :color="card.color" dark>
            <v-list-item two-line class="px-3">
              <v-list-item-content>
                <div class="overline mb-1" style="font-size: 0.7rem">
                  {{ card.label }}
                </div>
                <v-list-item-title class="headline font-weight-bold">
                  {{ card.count }}
                  <small class="caption">อัตรา</small>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-card>
        </v-col>
      </v-row>

      <!-- ═══════════════════════════════════════════════════════
           2) Validation Panel (ผลตรวจสอบความสมบูรณ์)
           ═══════════════════════════════════════════════════════ -->
      <v-card
        elevation="2"
        class="rounded-lg mb-4"
        :class="validationCardClass"
      >
        <v-toolbar flat dense>
          <v-icon :color="validationIconColor" class="mr-2">
            {{ validationIcon }}
          </v-icon>
          <v-toolbar-title class="text-subtitle-1 font-weight-bold">
            ผลการตรวจสอบความสมบูรณ์และข้อผิดพลาด (Data Validation)
          </v-toolbar-title>
          <v-spacer />

          <v-chip-group v-model="validationFilter" mandatory>
            <v-chip
              v-for="chip in filterChips"
              :key="chip.value"
              filter
              outlined
              small
              :value="chip.value"
              :color="chip.count > 0 ? chip.activeColor : 'grey'"
            >
              <v-icon v-if="chip.icon" x-small left>{{ chip.icon }}</v-icon>
              {{ chip.label }} ({{ chip.count }})
            </v-chip>
          </v-chip-group>
        </v-toolbar>

        <v-card-text class="pt-2 pb-3">
          <v-row dense>
            <!-- Critical Error: Ghost -->
            <v-col cols="12" sm="12" md="4" v-if="ghostItems.length > 0">
              <v-alert
                type="error"
                color="red darken-2"
                prominent
                border="left"
                class="mb-0 pulse-alert"
              >
                <div class="d-flex justify-space-between align-center">
                  <span class="font-weight-bold">
                    ข้อผิดพลาดร้ายแรง (Critical Error)
                  </span>
                  <v-btn
                    small
                    color="white"
                    light
                    @click="validationFilter = 'ghost'"
                  >
                    ดูรายการ ({{ ghostItems.length }})
                  </v-btn>
                </div>
                <div class="caption mt-2">
                  ระบุว่าเป็นอัตราว่าง แต่มีบุคลากรครองตัวตนอยู่จริงที่ปลายทาง
                  (มี ID Card, ชื่อ-สกุล) และไม่ได้ทำเรื่องย้ายออก
                </div>
              </v-alert>
            </v-col>

            <!-- Info: Domino -->
            <v-col cols="12" sm="12" md="4" v-if="dominoItems.length > 0">
              <v-alert type="info" dense text class="mb-0 h-100">
                <div class="d-flex justify-space-between align-center">
                  <span>
                    <strong>ตำแหน่งจากการคาย (ย้ายเวียน):</strong>
                    {{ dominoItems.length }} รายการ
                  </span>
                  <v-btn
                    x-small
                    text
                    color="info"
                    @click="validationFilter = 'domino'"
                  >
                    ตรวจสอบ
                  </v-btn>
                </div>
                <div class="caption mt-1">
                  ตำแหน่งที่ได้มาจากการที่เจ้าของเดิมย้ายไปที่อื่นในรอบนี้
                  (เป็นเหตุการณ์ปกติ)
                </div>
              </v-alert>
            </v-col>

            <!-- Duplicates + Missing Info -->
            <v-col cols="12" sm="12" md="4">
              <v-alert
                :type="duplicateGroups.length > 0 ? 'warning' : 'success'"
                dense
                text
                class="mb-2"
              >
                <div class="d-flex justify-space-between align-center">
                  <span>
                    <strong>เลขที่ตำแหน่งซ้ำ:</strong>
                    {{ duplicateGroups.length }} กลุ่ม
                  </span>
                  <v-btn
                    v-if="duplicateGroups.length > 0"
                    x-small
                    text
                    color="orange darken-2"
                    @click="validationFilter = 'duplicate'"
                  >
                    ตรวจสอบ
                  </v-btn>
                </div>
              </v-alert>
              <v-alert
                :type="missingInfoItems.length > 0 ? 'error' : 'success'"
                :color="missingInfoItems.length > 0 ? 'purple' : 'success'"
                dense
                text
                class="mb-0"
              >
                <div class="d-flex justify-space-between align-center">
                  <span>
                    <strong>ข้อมูลขาดหาย (ตำแหน่ง/กรณี):</strong>
                    {{ missingInfoItems.length }} รายการ
                  </span>
                </div>
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- ═══════════════════════════════════════════════════════
           3) Data Table (ตารางข้อมูลหลัก)
           ═══════════════════════════════════════════════════════ -->
      <base-material-card
        icon="mdi-briefcase-search"
        title="ข้อมูลอัตรากำลัง (รับย้าย)"
        color="primary"
        class="px-5 py-3"
      >
        <!-- Toolbar: Actions + Search -->
        <v-card flat class="grey lighten-5 rounded-lg mb-4 pa-4">
          <v-row align="center">
            <v-col cols="12" md="9" class="d-flex flex-wrap" style="gap: 10px">
              <v-btn small rounded color="primary" @click="openAddDialog">
                <v-icon left>mdi-plus-circle</v-icon> เพิ่มตำแหน่ง
              </v-btn>
              <v-btn
                small
                rounded
                color="success"
                outlined
                @click="exportToExcel"
              >
                <v-icon left>mdi-file-excel</v-icon> Export
              </v-btn>
              <v-divider vertical class="mx-2" />
              <v-btn small rounded color="indigo" dark @click="openCsvDialog">
                <v-icon left>mdi-file-import</v-icon> นำเข้า CSV
              </v-btn>
              <v-btn
                small
                rounded
                color="error"
                outlined
                @click="openClearDialog"
              >
                <v-icon left>mdi-delete-sweep</v-icon> ล้างข้อมูล
              </v-btn>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="ค้นหา (สถานศึกษา, ตำแหน่ง, เลขที่...)"
                outlined
                dense
                rounded
                hide-details
                class="bg-white"
              />
            </v-col>
          </v-row>
        </v-card>

        <v-data-table
          :headers="headers"
          :items="filteredItems"
          :search="search"
          :custom-filter="customSearch"
          :loading="loading"
          :item-class="rowClass"
          class="elevation-0 custom-table"
        >
          <!-- Validation Status -->
          <template v-slot:item.validation_status="{ item }">
            <div class="d-flex flex-column align-center gap-1 my-1">
              <v-tooltip right v-if="isGhost(item)">
                <template v-slot:activator="{ on }">
                  <v-chip
                    v-on="on"
                    color="red"
                    dark
                    x-small
                    label
                    class="font-weight-bold"
                  >
                    <v-icon x-small left>mdi-alert-octagon</v-icon>
                    มีคนครองตัวตนจริง
                  </v-chip>
                </template>
                <span>
                  ผิดพลาดร้ายแรง! ตำแหน่งนี้มี {{ item.frist_name }}
                  ครองอยู่แล้ว และไม่ได้ยื่นย้าย
                </span>
              </v-tooltip>

              <v-tooltip right v-if="isDomino(item) && !isGhost(item)">
                <template v-slot:activator="{ on }">
                  <v-chip v-on="on" color="info" dark x-small label>
                    <v-icon x-small left>mdi-sync</v-icon>
                    เวียน/คายตำแหน่ง
                  </v-chip>
                </template>
                <span>
                  ตำแหน่งนี้ได้มาจากการคายให้คนอื่นในรอบการย้ายนี้
                </span>
              </v-tooltip>

              <v-chip
                v-if="isDuplicate(item)"
                color="orange"
                dark
                x-small
                label
              >
                <v-icon x-small left>mdi-content-duplicate</v-icon>
                เลขซ้ำ
              </v-chip>

              <v-chip
                v-if="isMissingInfo(item)"
                color="purple"
                dark
                x-small
                label
              >
                <v-icon x-small left>mdi-file-hidden</v-icon>
                ข้อมูลขาด
              </v-chip>

              <v-icon v-if="isClean(item)" color="green" small>
                mdi-check-circle
              </v-icon>
            </div>
          </template>

          <!-- College Info -->
          <template v-slot:item.college_info="{ item }">
            <div class="font-weight-bold">{{ item.college_name }}</div>
            <div class="grey--text style-small">
              รหัส: {{ item.college_code }}
            </div>
          </template>

          <!-- Position Info -->
          <template v-slot:item.position_info="{ item }">
            <span class="font-weight-bold primary--text text-subtitle-1">
              {{ item.id_position || "-" }}
            </span>
            <div class="style-small grey--text text--darken-2">
              {{ item.position }}
            </div>
            <v-chip
              v-if="item.case_vacancy"
              x-small
              color="grey lighten-2"
              class="mt-1"
            >
              {{ item.case_vacancy }}
            </v-chip>
          </template>

          <!-- Booking Status -->
          <template v-slot:item.status_booking="{ item }">
            <div v-if="isBooked(item)" class="d-flex flex-column align-center">
              <v-chip
                color="orange lighten-4"
                text-color="orange darken-4"
                small
                class="font-weight-bold mb-1"
              >
                <v-icon left x-small>mdi-lock</v-icon> จองแล้ว
              </v-chip>
              <div
                class="caption grey--text text--darken-2"
                title="รหัสบัตรประชาชนผู้ที่จองตำแหน่งนี้"
              >
                {{ item.status_booking }}
              </div>
            </div>
            <div v-else class="text-center">
              <v-chip
                color="green lighten-4"
                text-color="green darken-4"
                small
                class="font-weight-bold"
              >
                <v-icon left x-small>mdi-check-circle</v-icon> ว่าง
              </v-chip>
            </div>
          </template>

          <!-- Actual Owner -->
          <template v-slot:item.actual_owner="{ item }">
            <div v-if="isGhost(item)" class="red--text text--darken-2">
              <v-icon small color="red">mdi-account-alert</v-icon>
              <strong>{{ item.frist_name }} {{ item.last_name }}</strong>
              <div class="style-small mt-1">ID: {{ item.id_card }}</div>
              <div
                class="style-small text-truncate"
                style="max-width: 150px"
                :title="item.college_name_self"
              >
                {{ item.college_name_self || "ไม่มีสังกัด" }}
              </div>
            </div>
            <div
              v-else-if="hasOwnerName(item)"
              class="grey--text text--darken-1"
            >
              {{ item.frist_name }} {{ item.last_name }}
              <br />
              <span style="font-size: 0.7rem">(เจ้าของเดิมกำลังจะย้าย)</span>
            </div>
            <span v-else class="grey--text">- ไม่มีคนครอง -</span>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-center">
              <v-btn
                icon
                color="amber darken-2"
                @click.stop="openEditDialog(item.id_m)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                color="error"
                @click.stop="openDeleteDialog(item.id_m)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </base-material-card>

      <!-- ═══════════════════════════════════════════════════════
           4) Dialogs (Add / Edit / Delete / Clear / CSV)
           ═══════════════════════════════════════════════════════ -->

      <!-- Dialog: เพิ่มตำแหน่ง -->
      <v-dialog v-model="addDialog" persistent max-width="50%">
        <v-card class="mx-auto pa-5">
          <base-material-card
            icon="mdi-account-multiple"
            title="เพิ่มข้อมูลอัตรากำลังรับย้าย"
            class="px-5 py-3 text_google"
          />
          <v-card-text>
            <v-form ref="addForm" lazy-validation>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="addFormData.college_code"
                      :items="colleges"
                      item-text="college_name"
                      item-value="college_code"
                      label="สถานศึกษา *"
                      :rules="requiredRule"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="addFormData.id_position"
                      label="เลขที่ตำแหน่ง *"
                      :rules="requiredRule"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="addFormData.position"
                      :items="userstatus"
                      item-text="user_status_name"
                      item-value="user_status_name"
                      label="ตำแหน่ง *"
                      :rules="requiredRule"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="addFormData.case_vacancy"
                      label="กรณี *"
                      :rules="requiredRule"
                    />
                  </v-col>
                </v-row>
              </v-container>
              <small>* จำเป็น</small>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="warning" large rounded @click="addDialog = false">
              <v-icon dark>mdi-close</v-icon> ยกเลิก
            </v-btn>
            <v-btn color="success" large rounded @click="submitAdd">
              <v-icon dark>mdi-content-save</v-icon>&nbsp;&nbsp;บันทึก
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog: แก้ไขตำแหน่ง -->
      <v-dialog v-model="editDialog" persistent max-width="80%">
        <v-card class="mx-auto pa-6">
          <base-material-card
            color="yellow"
            icon="mdi-clipboard-text"
            title="แก้ไขข้อมูลอัตรากำลัง"
            class="px-5 py-3 text_google"
          />
          <v-card-text>
            <v-form ref="editForm" lazy-validation>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="editFormData.college_code"
                      :items="colleges"
                      item-text="college_name"
                      item-value="college_code"
                      label="สถานศึกษา *"
                      :rules="requiredRule"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="editFormData.id_position"
                      label="เลขที่ตำแหน่ง *"
                      :rules="requiredRule"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="editFormData.position"
                      :items="userstatus"
                      item-text="user_status_name"
                      item-value="user_status_name"
                      label="ตำแหน่ง *"
                      :rules="requiredRule"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="editFormData.case_vacancy"
                      label="กรณี *"
                      :rules="requiredRule"
                    />
                  </v-col>
                </v-row>
              </v-container>
              <small>* จำเป็น</small>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn large rounded @click="editDialog = false">
              <v-icon dark>mdi-close</v-icon> ยกเลิก
            </v-btn>
            <v-btn color="warning" large rounded @click="submitEdit">
              <v-icon dark>mdi-pencil</v-icon>&nbsp;บันทึก
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog: ลบตำแหน่ง -->
      <v-dialog v-model="deleteDialog" persistent max-width="40%">
        <v-card class="mx-auto pa-5">
          <base-material-card
            color="error"
            icon="mdi-delete"
            title="ลบข้อมูลอัตรากำลัง"
            class="px-5 py-3 text_google"
          />
          <v-card-text class="text_google">
            <v-card>
              <v-card-text>
                <v-form ref="deleteForm" lazy-validation>
                  <p>
                    ยืนยันการลบข้อมูล<br />
                    <strong>
                      {{ editFormData.college_name }} :
                      {{ editFormData.id_position }}
                    </strong>
                  </p>
                </v-form>
              </v-card-text>
            </v-card>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn large @click="deleteDialog = false">
              <v-icon dark>mdi-close</v-icon> ยกเลิก
            </v-btn>
            <v-btn large color="red darken-3" dark @click="submitDelete">
              <v-icon dark>mdi-delete</v-icon>&nbsp;ลบ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog: ล้างข้อมูลทั้งหมด -->
      <v-dialog v-model="clearDialog" persistent max-width="40%">
        <v-card class="mx-auto pa-5">
          <base-material-card
            color="error"
            icon="mdi-delete"
            title="ล้างข้อมูลอัตรากำลังทั้งหมด"
            class="px-5 py-3 text_google"
          />
          <v-card-text class="text_google">
            <v-card>
              <v-card-text>
                <v-form ref="clearForm" lazy-validation>
                  <p class="red--text font-weight-bold">
                    ⚠ ยืนยันการล้างข้อมูลทั้งหมด<br />
                    การกระทำนี้ไม่สามารถย้อนกลับได้
                  </p>
                </v-form>
              </v-card-text>
            </v-card>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn large @click="clearDialog = false">
              <v-icon dark>mdi-close</v-icon> ยกเลิก
            </v-btn>
            <v-btn large color="red darken-3" dark @click="submitClear">
              <v-icon dark>mdi-delete</v-icon>&nbsp;ยืนยันการล้างข้อมูล
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog: นำเข้า CSV -->
      <v-dialog v-model="csvDialog" persistent max-width="50%">
        <v-card class="mx-auto pa-5">
          <base-material-card
            color="green"
            icon="mdi-content-paste"
            title="นำเข้าข้อมูลอัตรากำลังรับย้าย"
            class="px-5 py-3 text_google"
          />
          <v-card-text>
            <v-card>
              <v-card-text>
                <v-form ref="csvForm" lazy-validation>
                  <v-row>
                    <v-col cols="12">
                      <v-btn
                        block
                        large
                        color="info"
                        to="/admin/man_power_import/"
                        target="_blank"
                      >
                        <v-icon>mdi-exit-to-app</v-icon>
                        ส่งออกแบบฟอร์มบันทึกข้อมูล
                      </v-btn>
                    </v-col>
                    <v-col cols="12">
                      <v-file-input
                        v-model="csvFile"
                        accept=".csv"
                        name="csvFiles"
                        color="deep-purple accent-4"
                        counter
                        label="ไฟล์ .csv"
                        placeholder="ไฟล์ CSV อัตรากำลัง"
                        prepend-icon="mdi-paperclip"
                        outlined
                        rounded
                        :show-size="1000"
                      >
                        <template v-slot:selection="{ text }">
                          <v-chip color="deep-purple accent-4" dark label small>
                            {{ text }}
                          </v-chip>
                        </template>
                      </v-file-input>

                      <v-btn
                        block
                        large
                        color="green"
                        :disabled="!csvFile"
                        :loading="csvUploading"
                        @click="submitCsv"
                      >
                        <v-icon>mdi-book-plus</v-icon>
                        นำเข้า
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
            </v-card>
          </v-card-text>
          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn
                fab
                dark
                large
                color="grey"
                class="mx-2"
                @click="csvDialog = false"
              >
                <v-icon dark>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import Movementmenu from "../../components/admin/movementmenu.vue";
import * as XLSX from "xlsx";

export default {
  name: "ManPowerManagement",
  components: { Movementmenu },

  data() {
    return {
      // ── State ──
      loading: true,
      ApiKey: "HRvec2021",
      search: "",
      validationFilter: "all",

      // ── Data ──
      man_powers: [],
      colleges: [],
      provinces: [],
      regions: [],
      userstatus: [],

      // ── Dialogs ──
      addDialog: false,
      editDialog: false,
      deleteDialog: false,
      clearDialog: false,
      csvDialog: false,

      // ── Form Data ──
      addFormData: {},
      editFormData: {},

      // ── CSV ──
      csvFile: null,
      csvUploading: false,

      // ── Shared Validation Rules ──
      requiredRule: [v => !!v || "กรุณากรอกข้อมูล"],

      // ── Table Headers ──
      headers: [
        {
          text: "ผลตรวจสอบ",
          align: "center",
          value: "validation_status",
          sortable: false,
          width: "130px"
        },
        { text: "สถานศึกษา", align: "left", value: "college_info" },
        {
          text: "เลขที่ตำแหน่ง / กรณี",
          align: "left",
          value: "position_info"
        },
        {
          text: "สถานะการจอง",
          align: "center",
          value: "status_booking",
          width: "140px"
        },
        {
          text: "ผู้ครองตำแหน่ง (เจ้าของเดิม)",
          align: "left",
          value: "actual_owner"
        },
        {
          text: "จัดการ",
          align: "center",
          value: "actions",
          sortable: false
        }
      ]
    };
  },

  computed: {
    // ── Filtered Lists ──
    bookedItems() {
      return this.man_powers.filter(item => this.isBooked(item));
    },
    ghostItems() {
      return this.man_powers.filter(item => this.isGhost(item));
    },
    dominoItems() {
      return this.man_powers.filter(item => this.isDomino(item));
    },
    missingInfoItems() {
      return this.man_powers.filter(item => this.isMissingInfo(item));
    },

    // ── Duplicate Detection ──
    duplicateMap() {
      const countMap = {};
      for (const item of this.man_powers) {
        const key = String(item.id_position || "").trim();
        if (!key) continue;
        if (!countMap[key]) countMap[key] = [];
        countMap[key].push(item);
      }
      const dupMap = {};
      for (const [key, items] of Object.entries(countMap)) {
        if (items.length > 1) dupMap[key] = items;
      }
      return dupMap;
    },
    duplicateGroups() {
      return Object.entries(this.duplicateMap).map(([key, items]) => ({
        id_position: key,
        items
      }));
    },
    duplicateRefSet() {
      const refSet = new Set();
      for (const items of Object.values(this.duplicateMap)) {
        for (const item of items) refSet.add(item.id_m);
      }
      return refSet;
    },
    duplicateItemCount() {
      return this.duplicateRefSet.size;
    },

    // ── Aggregate Stats ──
    totalErrors() {
      const ids = new Set([
        ...this.ghostItems.map(i => i.id_m),
        ...this.missingInfoItems.map(i => i.id_m),
        ...this.duplicateRefSet
      ]);
      return ids.size;
    },
    cleanItemCount() {
      return this.man_powers.filter(item => this.isClean(item)).length;
    },

    // ── Summary Cards (ลดซ้ำซ้อนจาก template) ──
    summaryCards() {
      return [
        {
          key: "total",
          label: "อัตรากำลังทั้งหมด",
          count: this.man_powers.length,
          color: "blue darken-2",
          md: 2
        },
        {
          key: "booked",
          label: "ถูกจองแล้ว (มีผู้ลง)",
          count: this.bookedItems.length,
          color: "orange darken-3",
          md: 2
        },
        {
          key: "clean",
          label: "ว่างจริง (สมบูรณ์)",
          count: this.cleanItemCount,
          color: "success",
          md: 2
        },
        {
          key: "domino",
          label: "เวียน/คายตำแหน่ง",
          count: this.dominoItems.length,
          color: "info",
          md: 3
        },
        {
          key: "ghost",
          label: "ผิดพลาด (มีคนครอง)",
          count: this.ghostItems.length,
          color: this.ghostItems.length > 0 ? "red darken-3" : "grey",
          md: 3
        }
      ];
    },

    // ── Filter Chips (ลดซ้ำซ้อนจาก template) ──
    filterChips() {
      return [
        {
          value: "all",
          label: "ทั้งหมด",
          count: this.man_powers.length,
          activeColor: "primary",
          icon: null
        },
        {
          value: "booked",
          label: "จองแล้ว",
          count: this.bookedItems.length,
          activeColor: "orange darken-2",
          icon: "mdi-lock"
        },
        {
          value: "ghost",
          label: "ผิดพลาด:มีคนครอง",
          count: this.ghostItems.length,
          activeColor: "red",
          icon: "mdi-account-alert"
        },
        {
          value: "domino",
          label: "คายตำแหน่ง",
          count: this.dominoItems.length,
          activeColor: "info",
          icon: "mdi-sync"
        },
        {
          value: "duplicate",
          label: "เลขซ้ำ",
          count: this.duplicateItemCount,
          activeColor: "orange",
          icon: "mdi-content-duplicate"
        }
      ];
    },

    // ── Validation Card Styling ──
    validationCardClass() {
      if (this.ghostItems.length > 0) return "validation-card-critical";
      if (this.duplicateGroups.length > 0 || this.missingInfoItems.length > 0)
        return "validation-card-error";
      return "validation-card-ok";
    },
    validationIcon() {
      if (this.ghostItems.length > 0) return "mdi-alert-decagram";
      if (this.totalErrors > 0) return "mdi-shield-alert";
      return "mdi-shield-check";
    },
    validationIconColor() {
      if (this.ghostItems.length > 0) return "red";
      if (this.totalErrors > 0) return "orange";
      return "green";
    },

    // ── Filtered Table Items ──
    filteredItems() {
      const filterMap = {
        ghost: this.ghostItems,
        domino: this.dominoItems,
        missing: this.missingInfoItems,
        booked: this.bookedItems,
        duplicate: this.man_powers.filter(i => this.duplicateRefSet.has(i.id_m))
      };
      return filterMap[this.validationFilter] || this.man_powers;
    }
  },

  async mounted() {
    // เดิม fetchManPowers() (โหลดตารางหลัก) รอ await ต่อจาก Promise.all นี้เสมอ — ถ้า endpoint ใด
    // endpoint หนึ่งในนี้ค้าง (ไม่มี timeout) ตารางหลักจะไม่ถูกโหลดเลย ค้างอยู่ที่ loading:true
    // ตลอดไป (this.loading ถูกเซ็ต false เฉพาะใน fetchManPowers() เท่านั้น) ทั้งที่ข้อมูล 4 ตัวนี้
    // ใช้แค่เติม dropdown ใน dialog เพิ่ม/แก้ไข ไม่เกี่ยวกับการแสดงตารางหลักเลย — แยกให้ทำงาน
    // คู่ขนานกันแทน พร้อมใส่ timeout กันค้างถาวรทั้งสองฝั่ง
    this.loadReferenceData();
    await this.fetchManPowers();
  },

  methods: {
    // ════════════════════════════════════════════════════════
    //  API: Reference data (dropdowns เพิ่ม/แก้ไข — ไม่บล็อกตารางหลัก)
    // ════════════════════════════════════════════════════════
    async loadReferenceData() {
      try {
        const [collegeRes, provinceRes, regionRes, statusRes] = await Promise.all([
          this.$http.post(
            "college.php",
            { ApiKey: this.ApiKey },
            { timeout: 30000 }
          ),
          this.$http.post(
            "province.php",
            { ApiKey: this.ApiKey },
            { timeout: 30000 }
          ),
          this.$http.post(
            "region.php",
            { ApiKey: this.ApiKey },
            { timeout: 30000 }
          ),
          this.$http.post(
            "crud_user_status.php?crud=read",
            { ApiKey: this.ApiKey },
            { timeout: 30000 }
          )
        ]);
        this.colleges = collegeRes.data;
        this.provinces = provinceRes.data;
        this.regions = regionRes.data;
        this.userstatus = statusRes.data;
      } catch (err) {
        console.error("Failed to load reference data:", err);
      }
    },

    // ════════════════════════════════════════════════════════
    //  Validation Helpers
    // ════════════════════════════════════════════════════════
    isBooked(item) {
      return item.status_booking && item.status_booking.trim() !== "";
    },
    hasOwnerName(item) {
      return (
        (item.frist_name && item.frist_name.trim() !== "") ||
        (item.last_name && item.last_name.trim() !== "")
      );
    },
    isGhost(item) {
      return (
        item.frist_name &&
        item.frist_name.trim() !== "" &&
        item.id_card &&
        !item.is_transferring_out
      );
    },
    isDomino(item) {
      return (
        item.frist_name &&
        item.frist_name.trim() !== "" &&
        item.id_card &&
        !!item.is_transferring_out
      );
    },
    isMissingInfo(item) {
      return !item.id_position || !item.case_vacancy;
    },
    isDuplicate(item) {
      return this.duplicateRefSet.has(item.id_m);
    },
    isClean(item) {
      return (
        !this.isGhost(item) &&
        !this.isMissingInfo(item) &&
        !this.isDuplicate(item)
      );
    },

    // ════════════════════════════════════════════════════════
    //  Table Helpers
    // ════════════════════════════════════════════════════════
    rowClass(item) {
      if (this.isGhost(item)) return "critical-ghost-row";
      if (this.isDuplicate(item)) return "duplicate-row";
      if (this.isMissingInfo(item)) return "missing-info-row";
      if (this.isDomino(item)) return "domino-row";
      if (this.isBooked(item)) return "booked-row";
      return "";
    },

    customSearch(value, search, item) {
      if (!search) return true;
      const s = search.toLowerCase();
      return [
        item.college_name,
        item.college_code,
        item.id_position,
        item.position,
        item.case_vacancy,
        item.frist_name,
        item.last_name,
        item.id_card,
        item.status_booking
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(s);
    },

    // ════════════════════════════════════════════════════════
    //  API: Fetch Data
    // ════════════════════════════════════════════════════════
    async fetchManPowers() {
      this.loading = true;
      try {
        const result = await this.$http.post(
          "man_power.php",
          { ApiKey: this.ApiKey },
          { timeout: 30000 }
        );
        if (Array.isArray(result.data)) {
          this.man_powers = result.data;
        } else {
          // backend ตอบกลับ 200 แต่ไม่ใช่ array (เช่น {status:false, message:...})
          // เดิมโค้ดจะเงียบและแสดงตารางว่างเปล่าโดยไม่บอกสาเหตุเลย
          this.man_powers = [];
          Swal.fire({
            icon: "error",
            title: "โหลดข้อมูลไม่สำเร็จ",
            text: (result.data && result.data.message) || "",
            showConfirmButton: true
          });
        }
      } catch (err) {
        console.error("Failed to fetch man_powers:", err);
        this.man_powers = [];
        Swal.fire({
          icon: "error",
          title:
            err.code === "ECONNABORTED"
              ? "โหลดข้อมูลนานเกินไป กรุณาลองใหม่อีกครั้ง"
              : "โหลดข้อมูลไม่สำเร็จ",
          timer: 2000,
          showConfirmButton: false
        });
      } finally {
        this.loading = false;
      }
    },

    // ════════════════════════════════════════════════════════
    //  Syslog (ลดซ้ำซ้อน — เดิมเขียนซ้ำทุก method)
    // ════════════════════════════════════════════════════════
    async writeSyslog(event, detail) {
      try {
        const session =
          JSON.parse(localStorage.getItem("user") || "null") || {};
        await this.$http.post("data_syslog.insert.php", {
          ApiKey: this.ApiKey,
          user_account: session.user_name || "unknown",
          event_log: event,
          page_log: "man_power",
          table_log: "man_power",
          detail_log: detail,
          date_times: new Date()
            .toISOString()
            .slice(0, 19)
            .replace("T", " ")
        });
      } catch (err) {
        console.error("Syslog error:", err);
      }
    },

    // ════════════════════════════════════════════════════════
    //  Notification Helper
    // ════════════════════════════════════════════════════════
    notify(success, successMsg = "ดำเนินการเรียบร้อย") {
      Swal.fire({
        icon: success ? "success" : "warning",
        title: success ? successMsg : "ดำเนินการผิดพลาด",
        showConfirmButton: false,
        timer: 1500
      });
    },

    // ════════════════════════════════════════════════════════
    //  Dialog Openers
    // ════════════════════════════════════════════════════════
    openAddDialog() {
      this.addFormData = {};
      this.addDialog = true;
    },
    async openEditDialog(id_m) {
      try {
        const result = await this.$http.post("man_power.php", {
          ApiKey: this.ApiKey,
          id_m
        });
        this.editFormData = Array.isArray(result.data)
          ? result.data[0] || {}
          : result.data;
        this.editDialog = true;
      } catch (err) {
        console.error("Failed to load item for edit:", err);
      }
    },
    async openDeleteDialog(id_m) {
      try {
        const result = await this.$http.post("man_power.php", {
          ApiKey: this.ApiKey,
          id_m
        });
        this.editFormData = Array.isArray(result.data)
          ? result.data[0] || {}
          : result.data;
        this.deleteDialog = true;
      } catch (err) {
        console.error("Failed to load item for delete:", err);
      }
    },
    openClearDialog() {
      this.clearDialog = true;
    },
    openCsvDialog() {
      this.csvFile = null;
      this.csvDialog = true;
    },

    // ════════════════════════════════════════════════════════
    //  Submit: Add
    // ════════════════════════════════════════════════════════
    async submitAdd() {
      if (!this.$refs.addForm.validate()) return;
      try {
        this.addFormData.ApiKey = this.ApiKey;
        const result = await this.$http.post(
          "man_power.insert.php",
          this.addFormData
        );
        const ok = result.data.status === true;
        this.notify(ok, "ดำเนินการบันทึกข้อมูลเรียบร้อย");
        if (ok) {
          await this.writeSyslog(
            "insert",
            `${this.addFormData.id_position}:${this.addFormData.college_code}`
          );
        }
      } catch (err) {
        console.error("Add failed:", err);
        this.notify(false);
      } finally {
        this.addDialog = false;
        await this.fetchManPowers();
      }
    },

    // ════════════════════════════════════════════════════════
    //  Submit: Edit
    // ════════════════════════════════════════════════════════
    async submitEdit() {
      if (!this.$refs.editForm.validate()) return;
      try {
        this.editFormData.ApiKey = this.ApiKey;
        const result = await this.$http.post(
          "man_power.update.php",
          this.editFormData
        );
        const ok = result.data.status === true;
        this.notify(ok, "ดำเนินการแก้ไขข้อมูลเรียบร้อย");
        if (ok) {
          const d = this.editFormData;
          await this.writeSyslog(
            "update",
            `${d.id_position} : ${d.college_code} : ${d.case_vacancy}`
          );
        }
      } catch (err) {
        console.error("Edit failed:", err);
        this.notify(false);
      } finally {
        this.editDialog = false;
        await this.fetchManPowers();
      }
    },

    // ════════════════════════════════════════════════════════
    //  Submit: Delete
    // ════════════════════════════════════════════════════════
    async submitDelete() {
      try {
        this.editFormData.ApiKey = this.ApiKey;
        const result = await this.$http.post(
          "man_power.delete.php",
          this.editFormData
        );
        const ok = result.data.status === true;
        this.notify(ok, "ดำเนินการลบข้อมูลเรียบร้อย");
        if (ok) {
          const d = this.editFormData;
          await this.writeSyslog(
            "delete",
            `${d.id_position} : ${d.college_code} : ${d.case_vacancy}`
          );
        }
      } catch (err) {
        console.error("Delete failed:", err);
        this.notify(false);
      } finally {
        this.deleteDialog = false;
        await this.fetchManPowers();
      }
    },

    // ════════════════════════════════════════════════════════
    //  Submit: Clear All
    // ════════════════════════════════════════════════════════
    async submitClear() {
      try {
        const result = await this.$http.post("man_power.clear.php", {
          ApiKey: this.ApiKey
        });
        const ok = result.data.status === true;
        this.notify(ok, "ดำเนินการล้างข้อมูลเรียบร้อย");
        if (ok) {
          await this.writeSyslog("clear", "ล้างข้อมูลอัตรากำลังทั้งหมด");
        }
      } catch (err) {
        console.error("Clear failed:", err);
        this.notify(false);
      } finally {
        this.clearDialog = false;
        await this.fetchManPowers();
      }
    },

    // ════════════════════════════════════════════════════════
    //  Submit: CSV Import
    // ════════════════════════════════════════════════════════
    async submitCsv() {
      if (!this.csvFile) return;
      this.csvUploading = true;
      try {
        const formData = new FormData();
        const filename = `${Date.now()}.manpower.csv`;
        formData.append("file", this.csvFile);
        formData.append("filename", `../HRvecfiles/${filename}`);
        formData.append("ApiKey", this.ApiKey);
        const result = await this.$http.post("man_powercsv.php", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000
        });
        const ok = result.data.status === true;
        if (ok) {
          Swal.fire({
            icon: "success",
            title: "นำเข้าข้อมูลเรียบร้อย",
            text: `เพิ่มใหม่ ${result.data.inserted || 0} รายการ, อัปเดต ${result
              .data.updated || 0} รายการ, ข้าม ${result.data.skipped ||
              0} รายการ`,
            timer: 3000
          });
          await this.writeSyslog("csv_import", filename);
        } else {
          Swal.fire({
            icon: "warning",
            title: "นำเข้าข้อมูลไม่สำเร็จ",
            text: result.data.message || "",
            showConfirmButton: true
          });
        }
      } catch (err) {
        console.error("CSV import failed:", err);
        Swal.fire({
          icon: "error",
          title:
            err.code === "ECONNABORTED"
              ? "นำเข้าข้อมูลนานเกินไป ไฟล์อาจมีขนาดใหญ่เกินไป กรุณาแบ่งไฟล์แล้วลองใหม่"
              : "นำเข้าข้อมูลไม่สำเร็จ",
          showConfirmButton: true
        });
      } finally {
        this.csvUploading = false;
        this.csvDialog = false;
        await this.fetchManPowers();
      }
    },

    // ════════════════════════════════════════════════════════
    //  Export
    // ════════════════════════════════════════════════════════
    exportToExcel() {
      const data = this.filteredItems.map(item => ({
        ผลตรวจสอบ: this.isGhost(item)
          ? "ผิดพลาดร้ายแรง (มีคนครอง)"
          : this.isDomino(item)
          ? "คายตำแหน่ง"
          : this.isDuplicate(item)
          ? "เลขซ้ำ"
          : this.isMissingInfo(item)
          ? "ข้อมูลขาดหาย"
          : "ปกติ",
        รหัสสถานศึกษา: item.college_code,
        สถานศึกษา: item.college_name,
        เลขที่ตำแหน่ง: item.id_position,
        ตำแหน่ง: item.position,
        กรณีว่าง: item.case_vacancy,
        สถานะการจอง: this.isBooked(item)
          ? `จองโดย (${item.status_booking})`
          : "ว่าง",
        "ID Card คนครองปัจจุบัน": item.id_card || "-",
        ชื่อคนครองปัจจุบัน: this.hasOwnerName(item)
          ? `${item.frist_name || ""} ${item.last_name || ""}`.trim()
          : "-"
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Manpower_Validation");
      XLSX.writeFile(wb, `Manpower_Validation_${Date.now()}.xlsx`);
    }
  }
};
</script>

<style scoped>
/* ── Validation Card Border ── */
.validation-card-critical {
  border-top: 5px solid #d32f2f;
}
.validation-card-error {
  border-top: 4px solid #ff9800;
}
.validation-card-ok {
  border-top: 4px solid #4caf50;
}

/* ── Row Highlight Colors ── */
.critical-ghost-row {
  background-color: rgba(211, 47, 47, 0.08) !important;
  border-left: 5px solid #d32f2f !important;
}
.domino-row {
  background-color: rgba(3, 169, 244, 0.05) !important;
  border-left: 3px solid #03a9f4 !important;
}
.duplicate-row {
  background-color: rgba(255, 152, 0, 0.05) !important;
  border-left: 3px solid #ff9800 !important;
}
.missing-info-row {
  background-color: rgba(156, 39, 176, 0.05) !important;
  border-left: 3px solid #9c27b0 !important;
}
.booked-row {
  background-color: rgba(255, 152, 0, 0.02) !important;
}

/* ── Pulse Animation ── */
.pulse-alert {
  animation: pulse-border 2s infinite;
}
@keyframes pulse-border {
  0% {
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(211, 47, 47, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0);
  }
}

/* ── Table Header ── */
.custom-table >>> thead th {
  font-weight: 600 !important;
  background-color: #f8f9fa;
  white-space: nowrap;
}
.gap-1 {
  gap: 4px;
}
.h-100 {
  height: 100%;
}
</style>
